/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Badge, Collapse, Button } from 'reactstrap';
import type { InsightSynthesis } from '@g17eco/types/insights-synthesis';
import { InsightCard } from './InsightCard';
import { RecommendationRow } from './RecommendationRow';
import { ReferenceRow } from './ReferenceRow';
import LoadingButton from '@g17eco/molecules/button/LoadingButton';
import { InsightStatus } from '@g17eco/types/insights-synthesis';

interface Props {
  insight: InsightSynthesis;
  isOpen: boolean;
  onClose: () => void;
  highlightedActionIndex?: number; // When opened from Actions dashboard
  // Admin actions (optional)
  onApprove?: (insight: InsightSynthesis) => Promise<void>;
  onReject?: (insight: InsightSynthesis) => Promise<void>;
  isApproving?: boolean;
  isRejecting?: boolean;
}

export const InsightViewModal: React.FC<Props> = ({
  insight,
  isOpen,
  onClose,
  highlightedActionIndex,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}) => {
  const { content, status } = insight;
  const [referencesOpen, setReferencesOpen] = useState(false);

  const hasRecommendations = content.recommendations && content.recommendations.length > 0;
  const hasReferences = content.references && content.references.length > 0;
  const hasAdminActions = onApprove && onReject;
  const isPending = status === InsightStatus.PendingApproval;
  const isDisabled = isApproving || isRejecting;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size='xl'>
      <ModalHeader toggle={onClose}>
        <div className='d-flex align-items-center gap-2'>
          <i className='fas fa-lightbulb text-warning' />
          <span>Insight Details</span>
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Insight Card */}
        <div className='mb-4'>
          <InsightCard insight={insight} truncateTitle={false} truncateDescription={false} />
        </div>

        {hasRecommendations && (
          <div>
            <div
              className='btn btn-secondary w-100 border-0 d-flex justify-content-between align-items-center p-3 rounded'
              style={{ pointerEvents: 'none' }}
            >
              <div className='d-flex align-items-center gap-2 text-lg'>
                <i className='fas fa-tasks text-primary' />
                <span className='strong'>Recommended Actions</span>
                <Badge color='secondary'>{content.recommendations.length}</Badge>
              </div>
            </div>
            <div className='mt-2 p-2'>
              {content.recommendations.map((recommendation, index) => (
                <div key={index} className={`${highlightedActionIndex === index ? 'background-ThemeBgMedium' : ''}`}>
                  <RecommendationRow
                    action={recommendation}
                    index={index + 1}
                    selected={highlightedActionIndex === index}
                    colors={{ number: 'primary' }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* References Section - Collapsible */}
        {hasReferences && (
          <div className='mb-4'>
            <Button
              color='secondary'
              className='w-100 border-0 d-flex justify-content-between align-items-center p-3'
              onClick={() => setReferencesOpen(!referencesOpen)}
            >
              <div className='d-flex align-items-center gap-2 text-lg'>
                <i className='fas fa-link text-info' />
                <span className='strong'>Data References</span>
                <Badge color='secondary'>{content.references.length}</Badge>
              </div>
              <i className={`fas fa-chevron-${referencesOpen ? 'up' : 'down'}`} />
            </Button>
            <Collapse isOpen={referencesOpen}>
              <div className='mt-2 d-flex flex-column gap-2'>
                {content.references.map((reference, index) => (
                  <ReferenceRow key={index} reference={reference} index={index + 1} initiativeId={insight.initiativeId} />
                ))}
              </div>
            </Collapse>
          </div>
        )}

      </ModalBody>
      {hasAdminActions && (
        <ModalFooter>
          <LoadingButton
            color='success'
            onClick={() => onApprove(insight)}
            loading={isApproving || false}
            disabled={!isPending || isDisabled}
          >
            <i className='fa fa-check me-1' /> Approve
          </LoadingButton>
          <LoadingButton
            color='danger'
            onClick={() => onReject(insight)}
            loading={isRejecting || false}
            disabled={!isPending || isDisabled}
          >
            <i className='fa fa-times me-1' /> Reject
          </LoadingButton>
        </ModalFooter>
      )}
    </Modal>
  );
};
